/* eslint-disable @next/next/no-img-element */
import { ShoppingBagIcon, TrashIcon } from '@heroicons/react/24/outline';
import { MinusIcon, PlusIcon } from '@heroicons/react/16/solid';
import { Popover, PopoverBackdrop, PopoverButton, PopoverPanel } from '@headlessui/react';
import { Fragment, useActionState, useEffect, startTransition } from 'react';
import { useCart } from './cart-context';
import { createCartAndSetCookie, redirectToCheckout } from './actions';
import { useFormStatus } from 'react-dom';
import { formatPrice } from '@/lib/utils';
import { twMerge } from 'tailwind-merge';
import { updateItemQuantity } from 'components/cart/actions';
import Link from 'next/link';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/20/solid';
import { sendGAEvent } from '@next/third-parties/google';
import { LayoutTranslations } from '@/sanity/schemaTypes/objects/translations/layoutTranslationsType';
import Clarity from '@microsoft/clarity';

export default function Cart({
  locale,
  translations
}: {
  locale: string;
  translations: LayoutTranslations;
}) {
  const { cart, updateCartItem } = useCart();
  const [message, formAction, cartLoading] = useActionState(updateItemQuantity, null);

  useEffect(() => {
    if (!cart) {
      createCartAndSetCookie();
    }
  }, [cart]);

  const updateQuantity = (line: any, action: 'plus' | 'minus' | 'delete') => {
    const newQuantity =
      action === 'plus'
        ? line.quantity + 1
        : action === 'minus'
          ? Math.max(0, line.quantity - 1)
          : 0;
    const payload = {
      merchandiseId: line.merchandise.id,
      quantity: newQuantity,
      attributes: line.attributes
    };

    startTransition(async () => {
      const actionWithVariant = formAction.bind(null, payload);
      updateCartItem(line.merchandise.id, action, line.attributes);
      await actionWithVariant();
    });
  };

  return (
    <Popover className="text-sm lg:relative">
      {({ open }) => (
        <>
          <PopoverButton
            className={twMerge(
              'group -m-2 flex cursor-pointer items-center rounded-full border-2 border-transparent px-3 py-1',
              cart?.totalQuantity && 'border-primary shadow'
            )}
            onClick={() => {
              if (!open && process.env.VERCEL_ENV === 'production') {
                try {
                  sendGAEvent('event', 'view_cart', {});
                  Clarity.event('view_cart');
                } catch (error) {
                  console.warn(error);
                }
              }
            }}
          >
            <ShoppingBagIcon
              aria-hidden="true"
              className="size-6 shrink-0 text-gray-400 group-hover:text-gray-500"
            />
            <span className="ml-2 text-sm font-medium text-gray-700 group-hover:text-gray-800">
              {cart?.totalQuantity}
            </span>
            <span className="sr-only">{translations.cartButtonLabel}</span>
          </PopoverButton>
          <PopoverBackdrop className="fixed inset-0" />
          <PopoverPanel
            transition
            className="shadow-secondary/30 absolute top-24 right-[3vw] mt-px max-h-[calc(95vh-6rem)] w-[90vw] overflow-y-auto rounded-lg bg-white shadow-lg transition data-closed:opacity-0 data-enter:duration-200 data-enter:ease-out data-leave:duration-150 data-leave:ease-in lg:top-full lg:right-0 lg:left-auto lg:mt-3 lg:-mr-1.5 lg:max-w-[30rem] lg:ring-1 lg:ring-black/5"
          >
            <h2 className="sr-only">{translations.cartTitle}</h2>
            {!cart || cart.lines.length === 0 ? (
              <div className="grid place-content-center p-4 text-center">
                <div className="flex items-center justify-center">
                  <ShoppingBagIcon
                    aria-hidden="true"
                    className="mr-2 size-6 text-gray-400 group-hover:text-gray-500"
                  />
                  <p className="text-sm font-medium text-gray-700 group-hover:text-gray-800">
                    {translations.emptyCart}
                  </p>
                </div>
              </div>
            ) : (
              <>
                <ul className="divide-y divide-gray-200 px-4">
                  {cart.lines.map((line, i) => (
                    <Fragment key={i}>
                      {line.id && (
                        <li className="flex py-6">
                          <div className="size-24 shrink-0 overflow-hidden rounded-md border border-gray-200">
                            <img
                              alt={line.merchandise.product.title}
                              src={line.merchandise.product.featuredImage.url}
                              className="size-full object-cover"
                            />
                          </div>

                          <div className="ml-4 flex flex-1 flex-col justify-between">
                            <div>
                              <div className="flex justify-between text-base font-medium text-gray-900">
                                <h3>
                                  {/* TODO: add link to product page */}
                                  {line.merchandise.product.title}
                                </h3>
                                <p className="ml-4">
                                  {formatPrice(parseFloat(line.cost.totalAmount.amount), locale)}
                                </p>
                              </div>
                              <div className="flex flex-wrap gap-x-3">
                                <p className="mt-1 text-sm text-gray-500">
                                  {line.merchandise.title}
                                </p>
                                {line.attributes?.map((attr, i) => {
                                  if (attr.key.startsWith('data-')) return null;

                                  const isUrl = attr.value.match(/^\s*https?:\/\//);
                                  const lines = attr.value.split('\n');

                                  return (
                                    <Fragment key={i}>
                                      {isUrl
                                        ? lines.map((line, index) => (
                                            <Link
                                              key={index}
                                              href={line}
                                              className="mt-1 flex gap-1 text-sm text-blue-600 hover:underline"
                                              target="_blank"
                                            >
                                              {attr.key}
                                              <ArrowTopRightOnSquareIcon className="size-4" />
                                            </Link>
                                          ))
                                        : lines.map((line, index) => (
                                            <p className="mt-1 text-sm text-gray-500" key={index}>
                                              {attr.key} : {line}
                                            </p>
                                          ))}
                                    </Fragment>
                                  );
                                })}
                              </div>
                            </div>
                            <div className="mt-2 flex items-end justify-between gap-12 text-sm">
                              <div className="flex items-center rounded-md bg-gray-100 outline-1 -outline-offset-1 outline-gray-300">
                                <div className="grid shrink-0 grid-cols-1 focus-within:relative">
                                  <button
                                    type="button"
                                    onClick={() => updateQuantity(line, 'minus')}
                                    className="focus:outline-secondary col-start-1 row-start-1 w-full cursor-pointer appearance-none rounded-md px-2 py-1.5 focus:outline-2 focus:-outline-offset-2 sm:text-sm/6"
                                  >
                                    <MinusIcon
                                      aria-hidden="true"
                                      className="pointer-events-none col-start-1 row-start-1 size-5 self-center justify-self-end text-gray-500 sm:size-4"
                                    />
                                    <span className="sr-only">{translations.remove}</span>
                                  </button>
                                </div>
                                <div className="block max-w-18 appearance-none bg-white px-3 py-0.5 text-center text-base text-gray-600 focus:outline-none sm:text-sm/6">
                                  {line.quantity}
                                </div>
                                <div className="grid shrink-0 grid-cols-1 focus-within:relative">
                                  <button
                                    type="button"
                                    onClick={() => updateQuantity(line, 'plus')}
                                    className="focus:outline-secondary col-start-1 row-start-1 w-full cursor-pointer appearance-none rounded-md px-2 py-1.5 focus:outline-2 focus:-outline-offset-2 sm:text-sm/6"
                                  >
                                    <PlusIcon
                                      aria-hidden="true"
                                      className="pointer-events-none col-start-1 row-start-1 size-5 self-center justify-self-end text-gray-500 sm:size-4"
                                    />
                                    <span className="sr-only">{translations.add}</span>
                                  </button>
                                </div>
                              </div>

                              <div className="grid place-content-center">
                                <button
                                  type="button"
                                  onClick={() => updateQuantity(line, 'delete')}
                                  className="text-secondary cursor-pointer font-medium hover:text-indigo-500"
                                >
                                  <TrashIcon aria-hidden="true" className="mr-2 size-5" />
                                  <span className="sr-only">{translations.delete}</span>
                                </button>
                                <p aria-live="polite" className="sr-only" role="status">
                                  {message}
                                </p>
                              </div>
                            </div>
                          </div>
                        </li>
                      )}
                    </Fragment>
                  ))}
                </ul>

                <form
                  action={redirectToCheckout}
                  className="sticky bottom-0 bg-gradient-to-t from-white to-transparent px-4 pt-2 pb-6"
                >
                  <CheckoutButton
                    totalAmount={cart?.cost.totalAmount.amount}
                    locale={locale}
                    loading={cartLoading}
                    cart={cart}
                    translations={translations}
                  />
                </form>
              </>
            )}
          </PopoverPanel>
        </>
      )}
    </Popover>
  );
}

function CheckoutButton({
  totalAmount,
  locale,
  loading,
  cart,
  translations
}: {
  totalAmount?: string;
  locale: string;
  loading: boolean;
  cart: any;
  translations: LayoutTranslations;
}) {
  const { pending } = useFormStatus();

  return (
    <button
      type="submit"
      disabled={pending || loading}
      className="bg-primary hover:bg-secondary text-secondary focus:ring-secondary/80 w-full cursor-pointer rounded-md border border-transparent px-4 py-2 text-sm font-medium shadow-md duration-100 hover:text-white focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-50 focus:outline-hidden"
      onClick={() => {
        if (process.env.VERCEL_ENV === 'production') {
          try {
            sendGAEvent('event', 'begin_checkout', {
              currency: cart?.cost.totalAmount.currencyCode,
              value: parseFloat(cart?.cost.totalAmount.amount || '0'),
              items: cart?.lines.map((line: any, index: number) => ({
                item_id: line.merchandise.product.id,
                item_name: line.merchandise.product.title,
                price: parseFloat(line.cost.totalAmount.amount),
                quantity: line.quantity,
                item_variant: line.merchandise.title,
                index
              }))
            });
            Clarity.event('begin_checkout');
          } catch (error) {
            console.warn(error);
          }
        }
      }}
    >
      <span className="font-semibold">
        {pending
          ? translations.loading
          : loading
            ? translations.updatingCart
            : translations.confirmOrder}
      </span>
      {totalAmount && totalAmount !== 'NaN' ? (
        <span className="ml-1 opacity-80">({formatPrice(parseFloat(totalAmount), locale)})</span>
      ) : null}
    </button>
  );
}
